/**
 * Création et correction d'un site.
 *
 * Le client n'est choisi qu'à la création : le service refuse de le changer
 * ensuite, parce que déplacer un site emporterait ses machines et leur
 * historique.
 */
import { useEffect, useState, type FormEvent } from 'react';

import { ApiError, api, type Party, type Site } from '../api/client';
import { AvertissementDoublons } from './AvertissementDoublons';
import { Champ, ErreurFormulaire, Ligne, Panneau } from './Formulaire';
import { useDoublons } from './useDoublons';
import { doitInterrogerSite, doublonsDeLErreur, type Doublon } from '../domaine/doublons';

interface Saisie {
  customerId: string;
  name: string;
  code: string;
  addressLine1: string;
  postalCode: string;
  city: string;
  zone: string;
  accessNotes: string;
  openingHours: string;
  safetyNotes: string;
}

export function FormulaireSite({
  site,
  clientImpose,
  onFermer,
  onEnregistre,
}: {
  /** `null` = création. */
  site: Site | null;
  /** Pré-sélectionne le client, depuis sa fiche par exemple. */
  clientImpose?: string | undefined;
  onFermer: () => void;
  onEnregistre: (site: Site) => void;
}) {
  const [clients, setClients] = useState<Party[]>([]);
  const [saisie, setSaisie] = useState<Saisie>(() => ({
    customerId: site?.customerId ?? clientImpose ?? '',
    name: site?.name ?? '',
    code: site?.code ?? '',
    addressLine1: site?.addressLine1 ?? '',
    postalCode: site?.postalCode ?? '',
    city: site?.city ?? '',
    zone: site?.zone ?? '',
    accessNotes: site?.accessNotes ?? '',
    openingHours: site?.openingHours ?? '',
    safetyNotes: site?.safetyNotes ?? '',
  }));
  const [erreur, setErreur] = useState<ApiError | Error | null>(null);
  const [enCours, setEnCours] = useState(false);
  // Les fiches que le SERVEUR a rapprochées en refusant l'enregistrement (HTTP 409) : elles priment sur la
  // consultation faite pendant la saisie, et disparaissent dès que le client, le nom ou l'adresse change.
  const [doublonsServeur, setDoublonsServeur] = useState<readonly Doublon[] | null>(null);

  const identiteCourante = {
    customerId: site?.customerId ?? saisie.customerId,
    name: saisie.name,
    addressLine1: saisie.addressLine1,
    city: saisie.city,
  };
  const doublonsEnSaisie = useDoublons(
    doitInterrogerSite(
      identiteCourante,
      site
        ? {
            customerId: site.customerId,
            name: site.name,
            addressLine1: site.addressLine1 ?? '',
            city: site.city ?? '',
          }
        : null,
    )
      ? () =>
          api
            .doublonsSite({
              customerId: identiteCourante.customerId,
              name: saisie.name,
              addressLine1: saisie.addressLine1,
              city: saisie.city,
              excludeId: site?.id,
            })
            .then((r) => r.doublons)
      : null,
    JSON.stringify(identiteCourante),
  );
  const doublons = doublonsServeur ?? doublonsEnSaisie;

  useEffect(() => {
    if (site) return; // le client n'est plus modifiable en correction
    api
      .parties(undefined, 'CUSTOMER')
      .then(setClients)
      .catch(() => setErreur(new Error('Impossible de charger la liste des clients.')));
  }, [site]);

  const modifier = <K extends keyof Saisie>(cle: K, valeur: Saisie[K]) => {
    if (cle === 'customerId' || cle === 'name' || cle === 'addressLine1' || cle === 'city') {
      setDoublonsServeur(null);
    }
    setSaisie((s) => ({ ...s, [cle]: valeur }));
  };

  /** `confirmer` : l'utilisateur a vu l'avertissement de doublon et affirme que c'est un autre site. */
  const enregistrer = async (e: FormEvent | undefined, confirmer = false) => {
    e?.preventDefault();
    if (!saisie.name.trim()) {
      setErreur(new Error('Le nom du site est obligatoire.'));
      return;
    }
    if (!site && !saisie.customerId) {
      setErreur(new Error('Choisissez le client propriétaire du site.'));
      return;
    }

    setEnCours(true);
    setErreur(null);
    try {
      const commun = {
        name: saisie.name.trim(),
        code: vide(saisie.code),
        addressLine1: vide(saisie.addressLine1),
        postalCode: vide(saisie.postalCode),
        city: vide(saisie.city),
        zone: vide(saisie.zone),
        accessNotes: vide(saisie.accessNotes),
        openingHours: vide(saisie.openingHours),
        safetyNotes: vide(saisie.safetyNotes),
        ...(confirmer ? { confirmerDoublon: true } : {}),
      };

      const resultat = site
        ? await api.updateSite(site.id, commun)
        : await api.createSite({
            ...Object.fromEntries(Object.entries(commun).filter(([, v]) => v !== null)),
            customerId: saisie.customerId,
          });

      onEnregistre(resultat);
      onFermer();
    } catch (e) {
      // Un doublon probable n'est pas une panne : on montre les fiches existantes et on attend la décision.
      const rapprochees = doublonsDeLErreur(e);
      if (rapprochees) {
        setDoublonsServeur(rapprochees);
        setErreur(null);
      } else {
        setErreur(e instanceof ApiError ? e : new Error('Enregistrement impossible.'));
      }
    } finally {
      setEnCours(false);
    }
  };

  return (
    <Panneau
      titre={site ? 'Corriger le site' : 'Nouveau site'}
      sousTitre="Les consignes sont lues avant le départ en intervention."
      onFermer={onFermer}
      actions={
        <>
          <button className="secondaire" onClick={onFermer} disabled={enCours}>
            Annuler
          </button>
          <button onClick={(e) => void enregistrer(e)} disabled={enCours}>
            {enCours ? 'Enregistrement…' : site ? 'Enregistrer' : 'Créer le site'}
          </button>
        </>
      }
    >
      <form onSubmit={(e) => void enregistrer(e)}>
        <ErreurFormulaire
          message={erreur?.message ?? null}
          details={erreur instanceof ApiError ? erreur.details : undefined}
        />

        <AvertissementDoublons
          objet="site"
          doublons={doublons}
          correction={Boolean(site)}
          enCours={enCours}
          defiler={doublonsServeur !== null}
          onConfirmer={() => void enregistrer(undefined, true)}
        />

        {site ? (
          <Champ libelle="Client" aide="Non modifiable : un site ne se transfère pas d’un client à l’autre.">
            <input type="text" value={site.customerName} disabled />
          </Champ>
        ) : (
          <Champ libelle="Client" requis aide="Seuls les tiers ayant le rôle « Client » apparaissent.">
            <select
              value={saisie.customerId}
              onChange={(e) => modifier('customerId', e.target.value)}
              disabled={Boolean(clientImpose)}
            >
              <option value="">Choisir…</option>
              {clients.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.legalName}
                  {c.city ? ` — ${c.city}` : ''}
                </option>
              ))}
            </select>
          </Champ>
        )}

        <Ligne>
          <Champ libelle="Nom du site" requis aide="« Usine de Chambéry », « Bâtiment B »…">
            <input
              type="text"
              autoFocus
              value={saisie.name}
              onChange={(e) => modifier('name', e.target.value)}
            />
          </Champ>
          <Champ libelle="Code interne" aide="Celui du client, s’il en a un.">
            <input
              type="text"
              value={saisie.code}
              onChange={(e) => modifier('code', e.target.value)}
            />
          </Champ>
        </Ligne>

        <Champ libelle="Adresse">
          <input
            type="text"
            value={saisie.addressLine1}
            onChange={(e) => modifier('addressLine1', e.target.value)}
          />
        </Champ>

        <Ligne>
          <Champ libelle="Code postal">
            <input
              type="text"
              value={saisie.postalCode}
              onChange={(e) => modifier('postalCode', e.target.value)}
            />
          </Champ>
          <Champ libelle="Ville">
            <input
              type="text"
              value={saisie.city}
              onChange={(e) => modifier('city', e.target.value)}
            />
          </Champ>
        </Ligne>

        <Champ libelle="Zone" aide="Sert à répartir les tournées entre techniciens.">
          <input type="text" value={saisie.zone} onChange={(e) => modifier('zone', e.target.value)} />
        </Champ>

        <Champ libelle="Accès" aide="Badge, gardien, code portail, quai de livraison…">
          <textarea
            rows={2}
            value={saisie.accessNotes}
            onChange={(e) => modifier('accessNotes', e.target.value)}
          />
        </Champ>

        <Champ libelle="Horaires">
          <textarea
            rows={2}
            value={saisie.openingHours}
            onChange={(e) => modifier('openingHours', e.target.value)}
          />
        </Champ>

        <Champ libelle="Sécurité" aide="EPI, plan de prévention, habilitations, interdictions.">
          <textarea
            rows={3}
            value={saisie.safetyNotes}
            onChange={(e) => modifier('safetyNotes', e.target.value)}
          />
        </Champ>
      </form>
    </Panneau>
  );
}

function vide(v: string): string | null {
  return v.trim() === '' ? null : v.trim();
}
